// Builds the Affirm checkout object for the Virtual Card (VCN) flow from the
// BoxPay session. Affirm expects every amount as an integer number of cents.

// TEMP (sandbox only): Affirm VCN supports USD/CAD merchants only. Until a USD
// checkout session is wired up, coerce any other currency to USD and swap in a
// canned US shopper so the sandbox can still mint a virtual card. Delete this
// block (and its use below) before production.
const SANDBOX_FORCE_USD = true;
const AFFIRM_SUPPORTED_CURRENCIES = ["USD", "CAD"];
const SANDBOX_US_CONTACT = {
  address: {
    line1: "150 Spear Street",
    line2: "Suite 1800",
    city: "San Francisco",
    state: "CA",
    zipcode: "94105",
    country: "USA",
  },
  email: "test@example.com",
  phone_number: "+14155551234",
};

const toCents = (major) => Math.round(Number(major || 0) * 100);

const splitName = (shopper) => ({
  first: shopper?.firstName || "",
  last: shopper?.lastName || "",
});

const toAffirmAddress = (address) => ({
  line1: address?.address1 || "",
  line2: address?.address2 || undefined,
  city: address?.city || "",
  state: address?.state || "",
  zipcode: address?.postalCode || "",
  country: address?.countryCode || "",
});

const toAffirmItems = (items, itemUrl) => {
  if (!Array.isArray(items) || !items.length) {
    return [
      {
        display_name: "Order",
        sku: "order",
        unit_price: 0,
        qty: 1,
        item_url: itemUrl,
      },
    ];
  }
  return items.map((item) => ({
    display_name: item?.itemName || "Item",
    // session items carry no SKU/id — fall back to the name.
    sku: item?.itemName || "item",
    unit_price: toCents(item?.amountWithoutTax),
    qty: Number(item?.quantity) || 1,
    item_image_url: item?.imageUrl || undefined,
    item_url: itemUrl,
  }));
};

export const buildAffirmVcnCheckout = (sessionDetails, surchargeAmount = 0, updatedShopperDetails = {}) => {
  const pd = sessionDetails?.paymentDetails || {};
  // Prefer the shopper's edited details (address/contact form) over the stale
  // snapshot from session load — otherwise a just-edited address is ignored.
  const shopper = Object.keys(updatedShopperDetails || {}).length ? updatedShopperDetails : (pd.shopper || {});
  const order = pd.order || {};
  const address = shopper.deliveryAddress || {};

  const pageUrl = window.location.href;
  const confirmationUrl = pd.frontendReturnUrl || pageUrl;
  const cancelUrl = pd.frontendBackUrl || pageUrl;

  const sessionCurrency = pd.money?.currencyCode;
  const needsUsdOverride = SANDBOX_FORCE_USD && !AFFIRM_SUPPORTED_CURRENCIES.includes(sessionCurrency);
  const currency = needsUsdOverride ? "USD" : sessionCurrency;

  const contact = needsUsdOverride
    ? { name: splitName(shopper), ...SANDBOX_US_CONTACT }
    : {
      name: splitName(shopper),
      address: toAffirmAddress(address),
      email: shopper.email || undefined,
      phone_number: shopper.phoneNumber || undefined,
    };
  const total = (Number(pd.amountInMinorUnits) || toCents(pd.money?.amount)) + toCents(surchargeAmount);

  return {
    merchant: {
      name: sessionDetails?.merchantDetails?.merchantName || "BoxPay",
      user_confirmation_url: confirmationUrl,
      user_cancel_url: cancelUrl,
      user_confirmation_url_action: "GET",
      // Generates the virtual card on loan origination.
      use_vcn: true,
    },
    // Virtual Card only runs in modal mode.
    mode: "modal",
    shipping: contact,
    billing: contact,
    items: toAffirmItems(order.items, pageUrl),
    order_id: pd.context?.orderId || sessionDetails?.token,
    currency,
    shipping_amount: toCents(order.shippingAmount),
    tax_amount: toCents(order.taxAmount),
    total,
  };
};
